/**
 * Core logic for the Communication step: state, API hooks, template defaulting,
 * validation, and syncToPayload. Extracted from InterviewActivityCommunication
 * to keep the component focused on rendering.
 */
import {
  createRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
  type Ref,
} from 'react';
import { useSelector } from 'react-redux';
import type { EmailApiHandle, SelectOption } from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import type { InterviewActivityPayload } from '../types';
import type { MutableRefObject } from 'react';
import { INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE } from './types';
import {
  hydrateCommunicationStateFromPayload,
  toInterviewCommunicationPayload,
} from './helpers/payloadMap';
import {
  initialInterviewCommunicationFormState,
  interviewCommunicationReducer,
} from './state';
import {
  getFirstCommunicationErrorScrollTarget,
  scrollCommunicationSectionIntoView,
  type CommunicationSectionKey,
} from './scroll';
import type { CommunicationEmailApiRefs } from './panels/types';
import {
  hasValidationErrors,
  validateInterviewCommunicationForm,
  type CommunicationValidationVariables,
  type InterviewCommunicationValidationErrors,
} from './validate';
import { calendarInviteTemplateNameForApi } from './helpers/calendarInviteName';
import { compositionFromTemplateOption } from './helpers/templateApply';
import {
  cleanupStaleSenderValues,
  isBlankRichText,
} from './helpers/emailUtils';
import { useUploadUserInlineImage } from '@/api/userImageUploads';
import { useKFlags } from '@/hooks/use-k-flags';
import { selectSettings } from '@/store/bootstrap/selectors';
import { SHARED_EMAIL_VALUE_PREFIX } from '@/pages/InterviewManager/ScheduleInterview/hooks/useScheduleSenderOptions';
import {
  useInterviewActivityCommunicationVariables,
  useInterviewCommunicationSenderOptions,
  useInterviewEmailTemplateOptions,
} from './api';

interface SharedEmailRow {
  id: number;
  email?: string;
  name?: string;
}

function useSharedMailboxOptions(): SelectOption[] {
  const { feSharedEmails } = useKFlags();
  const settings = useSelector(selectSettings) as
    | { user?: { shared_emails?: SharedEmailRow[] } }
    | undefined;

  return useMemo<SelectOption[]>(() => {
    if (!feSharedEmails) return [];
    const rows = settings?.user?.shared_emails ?? [];
    return rows
      .filter(
        (row) =>
          typeof row?.id === 'number' && Number.isFinite(row.id) && row.id > 0
      )
      .map((row) => ({
        value: `${SHARED_EMAIL_VALUE_PREFIX}${row.id}`,
        // Name as the label, email as the secondary line — matches the
        // real-user rows. Fall back to email when the name is missing.
        label: row.name ?? row.email ?? `Shared mailbox ${row.id}`,
        description: row.name && row.email ? row.email : undefined,
        image: undefined,
      }));
  }, [feSharedEmails, settings?.user?.shared_emails]);
}

function useFromAndCcBccOptions(
  delegatableOptions: SelectOption[],
  sharedMailboxOptions: SelectOption[]
) {
  return useMemo(() => {
    // Real users only — the organizer sentinel is a template-time
    // placeholder that resolves to "whoever schedules" at runtime,
    // so it belongs on From only.
    const realUsers = delegatableOptions.filter(
      (o) => o.value !== INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
    );
    const organizer: SelectOption = {
      value: INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE,
      label: "Organizer's email",
    };
    return {
      fromOptions: [organizer, ...realUsers, ...sharedMailboxOptions],
      // CC / BCC can't use the sentinel — the organizer's own
      // address is already the From, so keep only real users here.
      ccBccOptions: realUsers,
    };
  }, [delegatableOptions, sharedMailboxOptions]);
}

interface UseCommunicationStepOptions {
  payloadRef: MutableRefObject<InterviewActivityPayload>;
  prefillVersion: number;
  ref: Ref<StepFormApi>;
}

export function useCommunicationStep({
  payloadRef,
  prefillVersion,
  ref,
}: UseCommunicationStepOptions) {
  // ── State ────────────────────────────────────────────────────────────────
  const [state, dispatch] = useReducer(
    interviewCommunicationReducer,
    initialInterviewCommunicationFormState,
    (base) =>
      hydrateCommunicationStateFromPayload(
        payloadRef.current.communication,
        base,
        { stripCalendarTemplates: prefillVersion > 0 }
      )
  );

  /**
   * Bumped on every `validate()` call so each Email re-fires
   * `focusFirstError` on repeat submits, even when the error map content is
   * unchanged. The empty→populated transition trigger handles the first
   * submit; this counter handles the rest.
   */
  const [submitAttempt, setSubmitAttempt] = useState(0);

  const [fieldErrors, setFieldErrors] =
    useState<InterviewCommunicationValidationErrors>({});

  /**
   * Stable per-section refs to each Email's imperative handle. `validate()`
   * calls `focusFirstError()` on the offending section's ref to scroll to the
   * bad field/variable — this bypasses the kula auto-scroll dedupe guard so the
   * correct editor is targeted with multiple editors on the page.
   */
  const emailApiRefs = useMemo<
    Record<
      CommunicationSectionKey,
      ReturnType<typeof createRef<EmailApiHandle>>
    >
  >(
    () => ({
      candidateCalendar: createRef<EmailApiHandle>(),
      candidateEmail: createRef<EmailApiHandle>(),
      interviewerCalendar: createRef<EmailApiHandle>(),
      interviewerEmail: createRef<EmailApiHandle>(),
      selfSchedule: createRef<EmailApiHandle>(),
      availability: createRef<EmailApiHandle>(),
    }),
    []
  );
  /** Debounces the scroll side-effect against the double `validate()` call
   *  (scheduleNow + the stepper's onStepChange re-validate). */
  const lastErrorScrollAtRef = useRef(0);

  const currUser = useSelector(selectCurrUser) as
    | { id?: number }
    | null
    | undefined;

  // ── API hooks ────────────────────────────────────────────────────────────
  const { data: candidateCalendarTemplateOptions = [] } =
    useInterviewEmailTemplateOptions('candidate-calendar');
  const { data: candidateEmailTemplateOptions = [] } =
    useInterviewEmailTemplateOptions('candidate-email');
  const { data: interviewerCalendarTemplateOptions = [] } =
    useInterviewEmailTemplateOptions('interviewer-calendar');
  const { data: interviewerEmailTemplateOptions = [] } =
    useInterviewEmailTemplateOptions('interviewer-email');
  const { data: selfScheduleTemplateOptions = [] } =
    useInterviewEmailTemplateOptions('self-schedule');
  const { data: availabilityTemplateOptions = [] } =
    useInterviewEmailTemplateOptions('candidate-availability');
  const { data: delegatableRaw = [], isFetched: isFromOptionsFetched } =
    useInterviewCommunicationSenderOptions();
  const sharedMailboxOptions = useSharedMailboxOptions();
  const { fromOptions, ccBccOptions } = useFromAndCcBccOptions(
    delegatableRaw,
    sharedMailboxOptions
  );

  const {
    candidateCalendar: candidateCalendarVariables,
    interviewerCalendar: interviewerCalendarVariables,
    candidateEmail: candidateEmailVariables,
    interviewerEmail: interviewerEmailVariables,
    selfScheduleEmail: selfScheduleVariables,
    candidateAvailabilityEmail: availabilityVariables,
  } = useInterviewActivityCommunicationVariables();

  const { mutateAsync: uploadUserInlineImageAsync } =
    useUploadUserInlineImage();

  const uploadInlineImage = useCallback(
    (file: File) => uploadUserInlineImageAsync(file),
    [uploadUserInlineImageAsync]
  );

  // ── Validation variables ─────────────────────────────────────────────────
  const validationVariables: CommunicationValidationVariables = useMemo(
    () => ({
      candidateCalendar: candidateCalendarVariables,
      interviewerCalendar: interviewerCalendarVariables,
      candidateEmail: candidateEmailVariables,
      interviewerEmail: interviewerEmailVariables,
      selfSchedule: selfScheduleVariables,
      availability: availabilityVariables,
    }),
    [
      candidateCalendarVariables,
      candidateEmailVariables,
      interviewerCalendarVariables,
      interviewerEmailVariables,
      selfScheduleVariables,
      availabilityVariables,
    ]
  );

  // After options arrive, drop any `from` / `cc` / `bcc` values whose
  // referenced user (or shared mailbox) is no longer in the picker —
  // happens when a teammate named in a saved plan is removed from the
  // org. Without this, the form silently carries the stale id, the
  // dropdown renders empty, and the save hits a backend
  // `err_*_sender_not_found` error.
  const didCleanupStaleRef = useRef(false);
  useEffect(() => {
    if (didCleanupStaleRef.current) return;
    if (!isFromOptionsFetched) return;
    didCleanupStaleRef.current = true;
    const fromOptionValues = new Set(fromOptions.map((o) => String(o.value)));
    const ccBccOptionValues = new Set(ccBccOptions.map((o) => String(o.value)));
    const cleanupOpts = { fromOptionValues, ccBccOptionValues };
    const candidateClean = cleanupStaleSenderValues(
      state.candidateEmail,
      cleanupOpts
    );
    if (candidateClean) {
      dispatch({ type: 'SET_CANDIDATE_EMAIL', payload: candidateClean });
    }
    const interviewerClean = cleanupStaleSenderValues(
      state.interviewerEmail,
      cleanupOpts
    );
    if (interviewerClean) {
      dispatch({
        type: 'SET_INTERVIEWER_EMAIL',
        payload: interviewerClean,
      });
    }
    const selfScheduleClean = cleanupStaleSenderValues(
      state.selfSchedule,
      cleanupOpts
    );
    if (selfScheduleClean) {
      dispatch({ type: 'SET_SELF_SCHEDULE', payload: selfScheduleClean });
    }
    const availabilityClean = cleanupStaleSenderValues(
      state.availability,
      cleanupOpts
    );
    if (availabilityClean) {
      dispatch({ type: 'SET_AVAILABILITY', payload: availabilityClean });
    }
    // One-shot, gated by ref — read state once and dispatch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFromOptionsFetched, fromOptions, ccBccOptions]);

  /** After a failed submit, keep error UI in sync while the user fixes fields. */
  useEffect(() => {
    setFieldErrors((prev) => {
      if (!hasValidationErrors(prev)) return prev;
      return validateInterviewCommunicationForm(state, validationVariables);
    });
  }, [state, validationVariables]);

  // ── Template defaulting (one-shot per block) ─────────────────────────────
  const didDefaultCandidateCal = useRef(false);
  const didDefaultInterviewerCal = useRef(false);
  const didDefaultCandidateEmail = useRef(false);
  const didDefaultInterviewerEmail = useRef(false);
  const didDefaultSelfSchedule = useRef(false);
  const didDefaultAvailability = useRef(false);

  useEffect(() => {
    if (prefillVersion > 0) return;
    if (!candidateCalendarTemplateOptions.length) return;
    if (state.candidateCalendar.template) {
      didDefaultCandidateCal.current = true;
      return;
    }
    if (didDefaultCandidateCal.current) return;
    didDefaultCandidateCal.current = true;
    dispatch({
      type: 'SET_CANDIDATE_CALENDAR',
      payload: compositionFromTemplateOption(
        state.candidateCalendar,
        candidateCalendarTemplateOptions[0]
      ),
    });
  }, [
    prefillVersion,
    candidateCalendarTemplateOptions,
    state.candidateCalendar,
  ]);

  useEffect(() => {
    if (prefillVersion > 0) return;
    if (!interviewerCalendarTemplateOptions.length) return;
    if (state.interviewerCalendar.template) {
      didDefaultInterviewerCal.current = true;
      return;
    }
    if (didDefaultInterviewerCal.current) return;
    didDefaultInterviewerCal.current = true;
    dispatch({
      type: 'SET_INTERVIEWER_CALENDAR',
      payload: compositionFromTemplateOption(
        state.interviewerCalendar,
        interviewerCalendarTemplateOptions[0]
      ),
    });
  }, [
    prefillVersion,
    interviewerCalendarTemplateOptions,
    state.interviewerCalendar,
  ]);

  useEffect(() => {
    if (!state.candidateEmailEnabled || !candidateEmailTemplateOptions.length)
      return;
    // Edit/legacy gate: skip only when the section already carries
    // real content — a template was picked, or a non-blank subject /
    // body was hydrated (created in the new feature). A legacy activity
    // (created before this feature was mandatory) hydrates blank, so it
    // falls through and gets the first template just like create mode.
    if (
      state.candidateEmail.template ||
      !isBlankRichText(state.candidateEmail.subject) ||
      !isBlankRichText(state.candidateEmail.content)
    ) {
      didDefaultCandidateEmail.current = true;
      return;
    }
    if (didDefaultCandidateEmail.current) return;
    didDefaultCandidateEmail.current = true;
    // PATCH (not SET) so we don't clobber `from` / `cc` / `bcc`
    // cleaned by the cleanupStaleSenderValues effect on the same
    // render — both effects read the closure-captured state, so a
    // SET here would race-overwrite the cleanup.
    const opt = candidateEmailTemplateOptions[0];
    dispatch({
      type: 'PATCH_CANDIDATE_EMAIL',
      payload: {
        template: opt.value,
        content: String(opt.data?.body ?? ''),
        subject: String(opt.data?.subject ?? ''),
      },
    });
  }, [
    candidateEmailTemplateOptions,
    state.candidateEmail,
    state.candidateEmailEnabled,
  ]);

  useEffect(() => {
    if (
      !state.interviewerEmailEnabled ||
      !interviewerEmailTemplateOptions.length
    )
      return;
    // See candidateEmail note: skip only when real content exists.
    if (
      state.interviewerEmail.template ||
      !isBlankRichText(state.interviewerEmail.subject) ||
      !isBlankRichText(state.interviewerEmail.content)
    ) {
      didDefaultInterviewerEmail.current = true;
      return;
    }
    if (didDefaultInterviewerEmail.current) return;
    didDefaultInterviewerEmail.current = true;
    // PATCH (not SET) — see note on candidateEmail above.
    const opt = interviewerEmailTemplateOptions[0];
    dispatch({
      type: 'PATCH_INTERVIEWER_EMAIL',
      payload: {
        template: opt.value,
        content: String(opt.data?.body ?? ''),
        subject: String(opt.data?.subject ?? ''),
      },
    });
  }, [
    interviewerEmailTemplateOptions,
    state.interviewerEmail,
    state.interviewerEmailEnabled,
  ]);

  useEffect(() => {
    if (!state.selfScheduleEnabled || !selfScheduleTemplateOptions.length)
      return;
    // Slot-selection email is mandatory + always on. A legacy activity
    // (created before this feature existed) hydrates blank from the
    // backend — fall through and prefill the first template so Subject
    // / body are never empty on edit. New-feature activities hydrate
    // with real content and are left untouched.
    if (
      state.selfSchedule.template ||
      !isBlankRichText(state.selfSchedule.subject) ||
      !isBlankRichText(state.selfSchedule.content)
    ) {
      didDefaultSelfSchedule.current = true;
      return;
    }
    if (didDefaultSelfSchedule.current) return;
    didDefaultSelfSchedule.current = true;
    // PATCH (not SET) — see note on candidateEmail above.
    const opt = selfScheduleTemplateOptions[0];
    dispatch({
      type: 'PATCH_SELF_SCHEDULE',
      payload: {
        template: opt.value,
        content: String(opt.data?.body ?? ''),
        subject: String(opt.data?.subject ?? ''),
      },
    });
  }, [
    selfScheduleTemplateOptions,
    state.selfSchedule,
    state.selfScheduleEnabled,
  ]);

  useEffect(() => {
    if (!state.availabilityEnabled || !availabilityTemplateOptions.length)
      return;
    // Availability email is always on. A legacy activity (created before
    // this feature existed) hydrates blank from the backend — fall through
    // and prefill the first template so Subject / body are never empty on
    // edit. New-feature activities hydrate with real content and are left
    // untouched.
    if (
      state.availability.template ||
      !isBlankRichText(state.availability.subject) ||
      !isBlankRichText(state.availability.content)
    ) {
      didDefaultAvailability.current = true;
      return;
    }
    if (didDefaultAvailability.current) return;
    didDefaultAvailability.current = true;
    // PATCH (not SET) — see note on candidateEmail above.
    const opt = availabilityTemplateOptions[0];
    dispatch({
      type: 'PATCH_AVAILABILITY',
      payload: {
        template: opt.value,
        content: String(opt.data?.body ?? ''),
        subject: String(opt.data?.subject ?? ''),
      },
    });
  }, [
    availabilityTemplateOptions,
    state.availability,
    state.availabilityEnabled,
  ]);

  // ── Imperative handle (validate + syncToPayload) ─────────────────────────
  useImperativeHandle(
    ref,
    () => ({
      validate: async () => {
        const errors = validateInterviewCommunicationForm(
          state,
          validationVariables
        );
        setFieldErrors(errors);
        /** Force every Email to surface its errors (red borders) on submit. */
        setSubmitAttempt((n) => n + 1);
        if (!hasValidationErrors(errors)) return true;

        const target = getFirstCommunicationErrorScrollTarget(errors);
        if (target) {
          // `validate()` runs twice per Save (scheduleNow + the stepper's
          // onStepChange re-validate). Debounce the scroll so we don't fire it
          // twice and fight ourselves.
          const now =
            typeof performance !== 'undefined' ? performance.now() : Date.now();
          const debounced = now - lastErrorScrollAtRef.current <= 250;
          const needsTabSwitch = state.activeSubTab !== target.tab;
          if (!debounced) {
            lastErrorScrollAtRef.current = now;
            if (needsTabSwitch) {
              dispatch({ type: 'SET_ACTIVE_SUB_TAB', payload: target.tab });
            }
            // Reveal the section, then land on the exact bad field/variable via
            // the target Email's imperative handle. `focusFirstError()` bypasses
            // the kula module-level auto-scroll dedupe guard, so the right
            // editor is targeted even with several editors mounted / a tab that
            // just switched.
            const runScroll = () => {
              scrollCommunicationSectionIntoView(target.sectionId);
              emailApiRefs[target.key].current?.focusFirstError();
            };
            if (needsTabSwitch) {
              // Editors stay mounted (`preservePanels`), so we only wait for the
              // newly-active panel to become visible/laid-out after the tab
              // switch — not for a fresh TipTap init. One short pass, plus a
              // cheap idempotent retry for layout-timing safety.
              window.setTimeout(runScroll, 120);
              window.setTimeout(runScroll, 260);
            } else {
              window.setTimeout(runScroll, 0);
            }
          }
        }
        return false;
      },
      syncToPayload: () => {
        const comm = toInterviewCommunicationPayload(state, {
          currentUserId: currUser?.id,
        });
        comm._ui_calendar_candidate_name = calendarInviteTemplateNameForApi(
          state.candidateCalendar,
          candidateCalendarTemplateOptions
        );
        comm._ui_calendar_interviewer_name = calendarInviteTemplateNameForApi(
          state.interviewerCalendar,
          interviewerCalendarTemplateOptions
        );
        payloadRef.current.communication = comm;
      },
    }),
    [
      candidateCalendarTemplateOptions,
      currUser?.id,
      dispatch,
      emailApiRefs,
      interviewerCalendarTemplateOptions,
      payloadRef,
      state,
      validationVariables,
    ]
  );

  // ── Panel props ──────────────────────────────────────────────────────────
  const panelVariableProps = useMemo(
    () => ({
      candidateCalendarVariables,
      interviewerCalendarVariables,
      candidateEmailVariables,
      interviewerEmailVariables,
      selfScheduleVariables,
      availabilityVariables,
      uploadInlineImage,
    }),
    [
      candidateCalendarVariables,
      candidateEmailVariables,
      interviewerCalendarVariables,
      interviewerEmailVariables,
      selfScheduleVariables,
      availabilityVariables,
      uploadInlineImage,
    ]
  );

  return {
    state,
    dispatch,
    fieldErrors,
    submitAttempt,
    candidateCalendarTemplateOptions,
    candidateEmailTemplateOptions,
    interviewerCalendarTemplateOptions,
    interviewerEmailTemplateOptions,
    selfScheduleTemplateOptions,
    availabilityTemplateOptions,
    fromOptions,
    ccBccOptions,
    panelVariableProps,
    emailApiRefs: emailApiRefs as CommunicationEmailApiRefs,
  };
}
